import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useAgentChat } from '@/hooks/use-agent-chat';
import { type SharedChatAdapter } from '@/types/agent-chat';

interface SiteChatConfig {
  externalMessage?: string;
  externalInjectedMessage?: string;
  loadChatHistory?: boolean;
}

interface SiteChatContextValue {
  adapter: SharedChatAdapter;
  config: SiteChatConfig;
  setConfig: (next: Partial<SiteChatConfig>) => void;
  resetConfig: (keys?: (keyof SiteChatConfig)[]) => void;
}

const SiteChatContext = createContext<SiteChatContextValue | null>(null);

export const useSiteChat = () => {
  const context = useContext(SiteChatContext);
  if (!context) {
    throw new Error('useSiteChat must be used within a SiteChatProvider');
  }
  return context;
};

export const SiteChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [config, setConfigState] = useState<SiteChatConfig>({
    loadChatHistory: false,
  });
  const [inputValue, setInputValue] = useState<string>('');

  const {
    state,
    sendMessage,
    sendMessageAs,
    loadingAgent,
    stopStreaming,
    groupMessagesByDate,
    historyPagination,
    setScrollToBottomCallback,
  } = useAgentChat(
    config.externalMessage,
    config.externalInjectedMessage,
    // Site chat is fire-and-forget POST — never fetch persisted history
    // even if a caller passes `loadChatHistory: true`. The hook also
    // hard-gates on `chatScope === 'dashboard'`; this is belt + braces.
    false,
    'site'
  );

  const setConfig = useCallback((next: Partial<SiteChatConfig>) => {
    setConfigState(prev => ({ ...prev, ...next }));
  }, []);

  const resetConfig = useCallback((keys?: (keyof SiteChatConfig)[]) => {
    if (!keys || keys.length === 0) {
      setConfigState({ loadChatHistory: false });
      return;
    }

    setConfigState(prev => {
      const next = { ...prev };
      keys.forEach(key => {
        if (key === 'loadChatHistory') {
          next.loadChatHistory = false;
          return;
        }
        delete next[key];
      });
      return next;
    });
  }, []);

  const adapter = useMemo<SharedChatAdapter>(
    () => ({
      state,
      sendMessage,
      sendMessageAs,
      loadingAgent,
      stopStreaming,
      groupMessagesByDate,
      historyPagination,
      setScrollToBottomCallback,
      inputValue,
      setInputValue,
    }),
    [
      state,
      sendMessage,
      sendMessageAs,
      loadingAgent,
      stopStreaming,
      groupMessagesByDate,
      historyPagination,
      setScrollToBottomCallback,
      inputValue,
    ]
  );

  const value = useMemo<SiteChatContextValue>(
    () => ({
      adapter,
      config,
      setConfig,
      resetConfig,
    }),
    [adapter, config, setConfig, resetConfig]
  );

  return (
    <SiteChatContext.Provider value={value}>
      {children}
    </SiteChatContext.Provider>
  );
};
